import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Autocomplete,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalculatorIcon from "@mui/icons-material/Calculate";
import EmailIcon from "@mui/icons-material/Email";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import useDuplicateEmailCheck from "./Utilities/useDuplicateEmailCheck";
import DuplicateEmailAlert from "./Utilities/DuplicateEmailAlert";
import { clearRegistrationEmailOnChange } from "./Utilities/registrationDuplicateEmail";
import { REGISTRATION_PAGE_UI_FIRST_TIME } from "./Utilities/registrationPageUi";
import { validateCorporateContactForm } from "./Utilities/registrationContactValidation";
import { finishGooglePlaceSelection } from "./Utilities/googlePlacesAddress";
import { RegistrationPasswordField } from "./Utilities/RegistrationPasswordField";
import { createLoadingAutocompleteSlotProps } from "./Utilities/registrationAutocompleteSlotProps";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiClient } from "../../globals/utils/api";
import { loadGoogleMaps } from "../../globals/utils/googleMapsLoader";
import PremiumCalculator from "./PremiumCalculatorPublic";
import SupportContactDialog from "../../components/SupportContactDialog";

const primaryColor = "#333333";
const secondaryColor = "#666666";
const COMPONENT_HEIGHT = "40px";

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "0px",
    backgroundColor: "rgba(249, 249, 249, 0.8)",
    "& fieldset": {
      borderColor: "#E0E0E0",
      borderWidth: "2px",
    },
    "&:hover fieldset": {
      borderColor: primaryColor,
    },
    "&.Mui-focused fieldset": {
      borderColor: primaryColor,
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "text.secondary",
    opacity: 0.7,
  },
  "& .MuiFormHelperText-root": {
    margin: "4px 0 0 0",
    fontSize: "0.75rem",
  },
};

const GoogleAutoComplete = ({ placeholder, callback }) => {
  const autoCompleteRef = useRef();
  const inputRef = useRef();
  const [loaded, setLoaded] = useState(false);

  const loadGoogleMapsScript = useCallback(() => {
    loadGoogleMaps()
      .then(() => setLoaded(true))
      .catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    loadGoogleMapsScript();
  }, [loadGoogleMapsScript]);

  useEffect(() => {
    if (loaded && window.google && inputRef.current) {
      // Add a small delay to ensure input is fully rendered
      setTimeout(() => {
        if (!inputRef.current) return;

        try {
          autoCompleteRef.current = new window.google.maps.places.Autocomplete(
            inputRef.current,
            {
              fields: ["address_components", "geometry"],
              types: ["address"],
            }
          );

          autoCompleteRef.current.addListener("place_changed", () => {
            const place = autoCompleteRef.current.getPlace();

            if (!place || !place.address_components) {
              console.error("No valid place returned or missing address components.");
              return;
            }

            finishGooglePlaceSelection(
              place,
              callback,
              () => new window.google.maps.Geocoder()
            );
          });
        } catch (error) {
          console.error("Error initializing Google Autocomplete:", error);
        }
      }, 100); // Small delay to ensure DOM is ready
    }
  }, [loaded, callback]);

  return (
    <>
      <TextField
        variant="outlined"
        fullWidth
        placeholder={placeholder}
        inputRef={inputRef}
        sx={textFieldSx}
        slotProps={{

          input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "text.secondary" }} />
            </InputAdornment>
          ),
          },
        }}
      />
      {/* CSS to ensure Google Maps dropdown is properly styled */}
      <style jsx global>{`
        .pac-container {
          z-index: 99999 !important;
          background: white !important;
          border: 1px solid #ccc !important;
          border-radius: 4px !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
          font-family: Arial, sans-serif !important;
        }
        .pac-item {
          padding: 8px 12px !important;
          cursor: pointer !important;
          border-bottom: 1px solid #eee !important;
          font-size: 14px !important;
          color: #333 !important;
        }
        .pac-item:last-child {
          border-bottom: none !important;
        }
        .pac-item:hover {
          background-color: #f5f5f5 !important;
        }
        .pac-item-query {
          font-weight: 600 !important;
          color: #333 !important;
        }
        .pac-matched {
          font-weight: bold !important;
          color: #1a73e8 !important;
        }
        .pac-icon {
          margin-right: 8px !important;
        }
        
        /* Ensure pac-container appears above MUI Dialog */
        .MuiDialog-root .pac-container {
          z-index: 99999 !important;
        }
      `}</style>
    </>
  );
};

// Phone number formatting
const formatPhoneNumber = (value) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, "");
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const FirstTimeRegister = () => {
  const [userType, setUserType] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingLawFirms, setLoadingLawFirms] = useState(false);
  const [loadingCitiesStates, setLoadingCitiesStates] = useState(false);
  const [openCalculator, setOpenCalculator] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);
  const [openLawFirmDialog, setOpenLawFirmDialog] = useState(false);

  // Corporate PG Step 2 — optional contact addition
  const [registrationStep, setRegistrationStep] = useState(1);
  const [corporateContacts, setCorporateContacts] = useState([]);
  const [contactForm, setContactForm] = useState({ full_name: "", email: "", phone_number: "", is_admin: false });
  const [contactErrors, setContactErrors] = useState({});

  // Individual bond request — "How did you find us?" tracking
  const [guestReferralSource, setGuestReferralSource] = useState("");
  const [guestReferralName, setGuestReferralName] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  // Common form data
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    position: "",
    phoneNumber: "",
    address: "",
    dateOfBirth: "",
    is_corporate_pg: false,
    business_name: "",
  });

  // Law firm specific data
  const [lawFirmData, setLawFirmData] = useState({
    lawFirm: null,
    lawFirmLocation: null,
  });

  const [allLawFirms, setAllLawFirms] = useState([]); // Cache all law firms
  const [lawFirmSearch, setLawFirmSearch] = useState("");
  const [lawFirmsFetched, setLawFirmsFetched] = useState(false);
  const [lawFirmLocations, setLawFirmLocations] = useState([]);
  const [loadingLawFirmLocations, setLoadingLawFirmLocations] = useState(false);
  const [lawFirmLocationDialogOpen, setLawFirmLocationDialogOpen] = useState(false);

  // New law firm creation (combined with location)
  const [newLawFirm, setNewLawFirm] = useState({
    name: "",
    website: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
  });
  const [fuzzyMatchDialogOpen, setFuzzyMatchDialogOpen] = useState(false);
  const [fuzzyMatchResult, setFuzzyMatchResult] = useState(null);

  // Cities/States dropdown data (used in the "Create New Law Firm & Location" dialog)
  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);

  // Error states
  const [errors, setErrors] = useState({});

  const duplicateEmailCheck = useDuplicateEmailCheck();
  const firstTimeSubmitDisabled = duplicateEmailCheck.isSubmitDisabled(loading);

  // Individual validation functions (similar to Register component)
  const { validateEmail, validateUsername } = REGISTRATION_PAGE_UI_FIRST_TIME.composeValidators(
    formData,
    setErrors
  );

  const validatePassword = () => {
    if (!formData.password.trim()) {
      setErrors(prev => ({ ...prev, password: "Password is required" }));
      return false;
    }
    if (formData.password.length < 8) {
      setErrors(prev => ({ ...prev, password: "Password must be at least 8 characters" }));
      return false;
    }
    if (!/\d/.test(formData.password)) {
      setErrors(prev => ({ ...prev, password: "Password must contain at least one number" }));
      return false;
    }
    if (!/[a-z]/.test(formData.password)) {
      setErrors(prev => ({ ...prev, password: "Password must contain at least one lowercase letter" }));
      return false;
    }
    if (!/[A-Z]/.test(formData.password)) {
      setErrors(prev => ({ ...prev, password: "Password must contain at least one uppercase letter" }));
      return false;
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      setErrors(prev => ({ ...prev, password: "Password must contain at least one special character" }));
      return false;
    }
    setErrors(prev => ({ ...prev, password: "" }));
    return true;
  };

  const validateConfirmPassword = () => {
    if (!formData.confirmPassword.trim()) {
      setErrors(prev => ({ ...prev, confirmPassword: "Please confirm your password" }));
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: "Passwords do not match" }));
      return false;
    }
    setErrors(prev => ({ ...prev, confirmPassword: "" }));
    return true;
  };

  const validateFirstName = () => {
    if (!formData.firstName.trim()) {
      setErrors(prev => ({ ...prev, firstName: "First name is required" }));
      return false;
    }
    setErrors(prev => ({ ...prev, firstName: "" }));
    return true;
  };

  const validateLastName = () => {
    if (!formData.lastName.trim()) {
      setErrors(prev => ({ ...prev, lastName: "Last name is required" }));
      return false;
    }
    setErrors(prev => ({ ...prev, lastName: "" }));
    return true;
  };

  const validatePosition = () => {
    if (userType === "lawfirm_user" && !formData.position.trim()) {
      setErrors(prev => ({ ...prev, position: "Position is required" }));
      return false;
    }
    setErrors(prev => ({ ...prev, position: "" }));
    return true;
  };

  const validatePhoneNumber = () => {
    if (userType === "professional_guardian" && !formData.phoneNumber.trim()) {
      setErrors(prev => ({ ...prev, phoneNumber: "Phone number is required" }));
      return false;
    }
    setErrors(prev => ({ ...prev, phoneNumber: "" }));
    return true;
  };

  const validateAddress = () => {
    if (userType === "professional_guardian" && !formData.address.trim()) {
      setErrors(prev => ({ ...prev, address: "Address is required" }));
      return false;
    }
    setErrors(prev => ({ ...prev, address: "" }));
    return true;
  };

  const validateDateOfBirth = () => {
    const isCorporatePg = userType === "professional_guardian" && formData.is_corporate_pg;
    if (!isCorporatePg && userType === "professional_guardian" && !formData.dateOfBirth.trim()) {
      setErrors(prev => ({ ...prev, dateOfBirth: "Date of birth is required" }));
      return false;
    }
    setErrors(prev => ({ ...prev, dateOfBirth: "" }));
    return true;
  };

  const validateBusinessName = () => {
    if (userType === "professional_guardian" && formData.is_corporate_pg && !formData.business_name.trim()) {
      setErrors(prev => ({ ...prev, business_name: "Business name is required for corporate entities" }));
      return false;
    }
    setErrors(prev => ({ ...prev, business_name: "" }));
    return true;
  };

  // Debounced search value
  const debouncedLawFirmSearch = useDebounce(lawFirmSearch, 300);

  // Fetch all law firms once and cache them
  const fetchAllLawFirms = async (forceRefetch = false) => {
    if (lawFirmsFetched && !forceRefetch) return; // Don't fetch if already fetched

    try {
      setLoadingLawFirms(true);
      const response = await apiClient("/list-public-law-firms/", "GET");
      const formattedLawFirms = response.map((firm) => ({
        label: firm.name,
        value: firm.lawfirm_id,
      }));

      setAllLawFirms(formattedLawFirms);
      setLawFirmsFetched(true);
    } catch (error) {
      console.error("Error fetching law firms:", error);
      toast.error("Failed to fetch law firms");
    } finally {
      setLoadingLawFirms(false);
    }
  };

  const fetchLocationsByLawFirm = useCallback(async (lawFirmId) => {
    if (!lawFirmId) {
      setLawFirmLocations([]);
      setLawFirmData(prev => ({ ...prev, lawFirmLocation: null }));
      return;
    }
    setLoadingLawFirmLocations(true);
    try {
      const response = await apiClient(
        `/get-locations-by-lawfirm/${lawFirmId}`,
        "GET"
      );
      const formatted = (response || []).map((loc) => ({
        value: loc.law_firm_location_id,
        name: loc.name,
        address: loc.address,
        city: loc.city,
        state: loc.state,
        zip_code: loc.zip_code,
        label: [loc.name, loc.address, [loc.city, loc.state, loc.zip_code].filter(Boolean).join(", ")].filter(Boolean).join(" — "),
      }));
      setLawFirmLocations(formatted);
      setLawFirmData(prev => {
        const next = { ...prev };
        if (formatted.length === 1) next.lawFirmLocation = formatted[0].value;
        return next;
      });
    } catch (err) {
      console.error("Error fetching law firm locations:", err);
      setLawFirmLocations([]);
      setLawFirmData(prev => ({ ...prev, lawFirmLocation: null }));
      toast.error("Failed to load law firm locations");
    } finally {
      setLoadingLawFirmLocations(false);
    }
  }, []);

  useEffect(() => {
    if (userType !== "lawfirm_user") return;
    const id = lawFirmData.lawFirm;
    if (!id) {
      setLawFirmLocations([]);
      setLawFirmData(prev => ({ ...prev, lawFirmLocation: null }));
      return;
    }
    fetchLocationsByLawFirm(id);
  }, [userType, lawFirmData.lawFirm, fetchLocationsByLawFirm]);

  // Filter law firms based on search term
  const filteredLawFirms = useMemo(() => {
    if (!allLawFirms.length) return [];

    // Only show results when user has typed at least 3 characters
    if (debouncedLawFirmSearch.trim().length < 3) {
      return [];
    }

    return allLawFirms
      .filter(firm =>
        firm.label.toLowerCase().includes(debouncedLawFirmSearch.toLowerCase())
      )
      .slice(0, 10); // Limit to 5 results
  }, [allLawFirms, debouncedLawFirmSearch]);

  const selectedLawFirmLocation = useMemo(
    () => lawFirmLocations.find((l) => l.value === lawFirmData.lawFirmLocation) ?? null,
    [lawFirmLocations, lawFirmData.lawFirmLocation]
  );

  // Handle law firm creation (with location)
  const handleAddLawFirm = async () => {
    try {
      setLoadingLawFirms(true);
      const fuzzyRes = await apiClient("/fuzzy-match-lawfirm", "POST", {
        name: newLawFirm.name,
      });
      const matchedId = fuzzyRes?.match?.lawfirm_id ?? fuzzyRes?.law_firm?.id ?? fuzzyRes?.lawfirm_id;
      const matchedName = fuzzyRes?.match?.name ?? fuzzyRes?.law_firm?.name ?? fuzzyRes?.law_firm_name ?? "";
      const hasMatch = (fuzzyRes?.match != null && matchedId != null) || (fuzzyRes?.matched && matchedId != null);
      if (hasMatch) {
        setLoadingLawFirms(false);
        setFuzzyMatchResult({ id: matchedId, name: matchedName || newLawFirm.name });
        setFuzzyMatchDialogOpen(true);
        return;
      }
      // Step 1: Create Law Firm using FormData
      const formData = new FormData();
      formData.append("name", newLawFirm.name);
      formData.append("website", newLawFirm.website || "");
      formData.append("accounting_contact_name", "");
      formData.append("accounting_contact_email", "");
      formData.append("accounting_contact_phone", "");
      formData.append("is_new", true);
      formData.append("is_active", true);
      formData.append("created_by_guest", true);

      const lawFirmResponse = await apiClient(
        "/create-a-lawfirm",
        "POST",
        formData,
        {},
        { "content-type": "multipart/form-data" }
      );

      // Step 2: Create Location for the Law Firm
      await apiClient("/lawfirm-location/", "POST", [
        {
          lawfirm_id: lawFirmResponse.lawfirm_id,
          name: "Main Location",
          address: newLawFirm.address,
          zip_code: newLawFirm.zip,
          city: newLawFirm.city,
          county: "",
          state: newLawFirm.state,
          phone: newLawFirm.phone,
          is_active: true,
        },
      ]);

      // Add the new law firm to the cached list and select it
      const newFirm = {
        label: newLawFirm.name,
        value: lawFirmResponse.lawfirm_id,
      };

      setAllLawFirms(prev => [newFirm, ...prev]);
      setLawFirmData(prev => ({ ...prev, lawFirm: lawFirmResponse.lawfirm_id }));

      setLawFirmSearch("");
      setErrors(prev => ({ ...prev, lawFirm: "" }));

      toast.success("Law Firm and location added successfully!");
      handleCloseLawFirmDialog();
    } catch (error) {
      console.error("Error adding law firm:", error);
      toast.error("Failed to add law firm!");
    } finally {
      setLoadingLawFirms(false);
    }
  };

  const handleUseExistingLawFirm = () => {
    if (!fuzzyMatchResult) return;
    const existingFirm = { label: fuzzyMatchResult.name, value: fuzzyMatchResult.id };
    setAllLawFirms(prev => {
      const has = prev.some(f => f.value === fuzzyMatchResult.id);
      return has ? prev : [existingFirm, ...prev];
    });
    setLawFirmData(prev => ({ ...prev, lawFirm: fuzzyMatchResult.id, lawFirmLocation: null }));
    setLawFirmSearch("");
    setErrors(prev => ({ ...prev, lawFirm: "" }));
    setFuzzyMatchDialogOpen(false);
    setFuzzyMatchResult(null);
    handleCloseLawFirmDialog();
    toast.success("Using existing law firm.");
  };

  const doCreateLawFirmAndLocation = async () => {
    const formData = new FormData();
    formData.append("name", newLawFirm.name);
    formData.append("website", newLawFirm.website || "");
    formData.append("accounting_contact_name", "");
    formData.append("accounting_contact_email", "");
    formData.append("accounting_contact_phone", "");
    formData.append("is_new", true);
    formData.append("is_active", true);
    formData.append("created_by_guest", true);
    const lawFirmResponse = await apiClient(
      "/create-a-lawfirm",
      "POST",
      formData,
      {},
      { "content-type": "multipart/form-data" }
    );
    await apiClient("/lawfirm-location/", "POST", [
      {
        lawfirm_id: lawFirmResponse.lawfirm_id,
        name: "Main Location",
        address: newLawFirm.address,
        zip_code: newLawFirm.zip,
        city: newLawFirm.city,
        county: "",
        state: newLawFirm.state,
        phone: newLawFirm.phone,
        is_active: true,
      },
    ]);
    const newFirm = { label: newLawFirm.name, value: lawFirmResponse.lawfirm_id };
    setAllLawFirms(prev => [newFirm, ...prev]);
    setLawFirmData(prev => ({ ...prev, lawFirm: lawFirmResponse.lawfirm_id }));
    setLawFirmSearch("");
    setErrors(prev => ({ ...prev, lawFirm: "" }));
    toast.success("Law Firm and location added successfully!");
    handleCloseLawFirmDialog();
  };

  const handleCreateNewLawFirmAnyway = async () => {
    setFuzzyMatchDialogOpen(false);
    setFuzzyMatchResult(null);
    try {
      setLoadingLawFirms(true);
      await doCreateLawFirmAndLocation();
    } catch (error) {
      console.error("Error adding law firm:", error);
      toast.error("Failed to add law firm!");
    } finally {
      setLoadingLawFirms(false);
    }
  };

  // Handle address callback for law firm creation dialog
  const handleLawFirmAddressCallback = (address, city, state, zipCode) => {
    setNewLawFirm(prev => ({
      ...prev,
      address,
      city,
      state,
      zip: zipCode,
    }));
  };

  // Comprehensive form validation using individual validation functions
  const validateForm = () => {
    let isValid = true;

    // Validate common fields
    if (!validateEmail()) isValid = false;
    if (!validateUsername()) isValid = false;
    if (!validatePassword()) isValid = false;
    if (!validateFirstName()) isValid = false;
    if (!validateLastName()) isValid = false;

    // Validate law firm specific fields
    if (userType === "lawfirm_user" || userType === "independent_paralegal") {
      if (!lawFirmData.lawFirm) {
        setErrors(prev => ({ ...prev, lawFirm: "Please select a law firm" }));
        isValid = false;
      }
      if (lawFirmData.lawFirm && lawFirmLocations.length > 0 && !lawFirmData.lawFirmLocation) {
        setErrors(prev => ({ ...prev, lawFirmLocation: "Please select a law firm location" }));
        isValid = false;
      }
      if (userType === "lawfirm_user" && !validatePosition()) isValid = false;
    }

    // Validate type-specific fields
    if (userType === "professional_guardian") {
      if (!validatePhoneNumber()) isValid = false;
      if (!validateAddress()) isValid = false;
      if (!validateDateOfBirth()) isValid = false;
      if (formData.is_corporate_pg && !validateBusinessName()) isValid = false;
    }

    return isValid;
  };

  // Handle form submission
  const handleRegister = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields correctly.");
      return;
    }

    setLoading(true);
    try {
      let payload;
      let endpoint;

      if (userType === "lawfirm_user") {
        payload = {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          first_name: formData.firstName,
          last_name: formData.lastName,
          lawfirm_id: lawFirmData.lawFirm,
          ...(lawFirmData.lawFirmLocation != null && { law_firm_location_id: lawFirmData.lawFirmLocation }),
          position: formData.position,
          user_type: "lawfirm_user",
        };
        endpoint = "/register-lawfirm-guardian"
      } else if (userType === "professional_guardian") {
        payload = {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          first_name: formData.firstName,
          last_name: formData.lastName,
          user_type: "professional_guardian",
          phone_number: formData.phoneNumber,
          address: formData.address,
          is_corporate_pg: formData.is_corporate_pg,
          business_name: formData.is_corporate_pg ? formData.business_name : "",
          ...(formData.is_corporate_pg ? {} : { date_of_birth: formData.dateOfBirth }),
        };
        endpoint = "/register-lawfirm-guardian"
      } else if (userType === "independent_paralegal") {
        payload = {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          first_name: formData.firstName,
          last_name: formData.lastName,
          user_type: "independent_paralegal",
          lawfirm_id: lawFirmData.lawFirm,
          ...(lawFirmData.lawFirmLocation != null && { law_firm_location_id: lawFirmData.lawFirmLocation }),
        };
        endpoint = "/register-lawfirm-guardian"
      }

      // For corporate PGs on Step 1, advance to Step 2 (contact addition) without calling the API
      if (userType === "professional_guardian" && formData.is_corporate_pg && registrationStep === 1) {
        setRegistrationStep(2);
        setLoading(false);
        return;
      }

      if (userType === "professional_guardian" && formData.is_corporate_pg && corporateContacts.length > 0) {
        payload.contacts = corporateContacts.map(c => ({
          full_name: c.full_name,
          email: c.email,
          phone_number: c.phone_number || "",
          is_admin: c.is_admin,
        }));
      }

      await apiClient(endpoint, "POST", payload);
      toast.success("Registration successful! Please login to continue.");
      navigate("/");
    } catch (error) {
      console.error("Registration error:", error);
      if (error.username && error.username[0] === "vermost portal user with this username already exists.") {
        toast.error(`Registration failed. Please try a different username.`);
      }
      else if (error.non_field_errors && error.non_field_errors[0] === "A professional guardian with this email already exists.") {
        toast.error("Registration Failed. A professional guardian with this email already exists. Please contact support for assistance.")
      }
      else {
        toast.error(`Registration failed. Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Google address callback for professional guardian
  const handleAddressCallback = (address, city, state, zipCode) => {
    // Format address like "P.O. Box 24, St. Petersburg, Florida, 33731"
    const formattedAddress = `${address}, ${city}, ${state}, ${zipCode}`;
    setFormData(prev => ({ ...prev, address: formattedAddress }));
    // Clear address error when address is selected
    if (errors.address) {
      setErrors(prev => ({ ...prev, address: "" }));
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "phoneNumber") {
      const formatted = formatPhoneNumber(value);
      setFormData({ ...formData, [name]: formatted });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    // Clear error when user starts typing (similar to Register component)
    switch (name) {
      case "email":
        if (errors.email) setErrors(prev => ({ ...prev, email: "" }));
        clearRegistrationEmailOnChange(duplicateEmailCheck, name, "email");
        break;
      case "username":
        if (errors.username) setErrors(prev => ({ ...prev, username: "" }));
        break;
      case "password":
        if (errors.password) setErrors(prev => ({ ...prev, password: "" }));
        break;
      case "confirmPassword":
        if (errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: "" }));
        break;
      case "firstName":
        if (errors.firstName) setErrors(prev => ({ ...prev, firstName: "" }));
        break;
      case "lastName":
        if (errors.lastName) setErrors(prev => ({ ...prev, lastName: "" }));
        break;
      case "position":
        if (errors.position) setErrors(prev => ({ ...prev, position: "" }));
        break;
      case "phoneNumber":
        if (errors.phoneNumber) setErrors(prev => ({ ...prev, phoneNumber: "" }));
        break;
      case "address":
        if (errors.address) setErrors(prev => ({ ...prev, address: "" }));
        break;
      case "dateOfBirth":
        if (errors.dateOfBirth) setErrors(prev => ({ ...prev, dateOfBirth: "" }));
        break;
      case "business_name":
        if (errors.business_name) setErrors(prev => ({ ...prev, business_name: "" }));
        break;
      default:
        break;
    }
  };

  // Handle key down events (similar to Register component)
  const handleKeyDown = (event) => {
    if (event.key === "Enter") handleRegister();
  };

  // Dialog handlers
  const handleOpenLawFirmDialog = () => setOpenLawFirmDialog(true);
  const handleCloseLawFirmDialog = () => {
    setOpenLawFirmDialog(false);
    setNewLawFirm({
      name: "",
      website: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      phone: "",
    });
  };

  // Load law firms when user selects law firm option
  useEffect(() => {
    if (userType === "lawfirm_user" || userType === "independent_paralegal") {
      fetchAllLawFirms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  // Fetch cities/states (same APIs used in ProbateBond) for dropdown options
  useEffect(() => {
    let cancelled = false;
    const fetchCitiesAndStates = async () => {
      try {
        setLoadingCitiesStates(true);
        const [cities, states] = await Promise.all([
          apiClient("/fields/city/", "GET"),
          apiClient("/fields/states/", "GET"),
        ]);
        if (cancelled) return;
        setAllCities(Array.isArray(cities) ? cities : []);
        setAllStates(Array.isArray(states) ? states : []);
      } catch (error) {
        console.error("Error loading cities or states:", error);
      } finally {
        if (!cancelled) setLoadingCitiesStates(false);
      }
    };
    fetchCitiesAndStates();
    return () => {
      cancelled = true;
    };
  }, []);



  return (
    <Box
      display="flex"
      flexDirection="row"
      height="100vh"
      sx={{
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Left Column - Hero Section */}
      <Box
        sx={{
          width: isMobile ? "0%" : "60%",
          display: isMobile ? "none" : "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          background:
            "linear-gradient(135deg, #000000 0%, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%, #000000 100%)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 30% 30%, rgba(51, 51, 51, 0.1) 0%, transparent 50%)",
            pointerEvents: "none",
          },
        }}
      >
        {/* Decorative Elements */}
        <Box
          sx={{
            position: "absolute",
            top: "15%",
            left: "10%",
            width: "120px",
            height: "120px",
            borderRadius: "50%",
            background:
              "linear-gradient(45deg, rgba(51, 51, 51, 0.3), rgba(102, 102, 102, 0.1))",
            filter: "blur(40px)",
            animation: "float 6s ease-in-out infinite",
            "@media (max-width: 768px)": {
              display: "none",
            },
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "25%",
            right: "10%",
            width: "180px",
            height: "180px",
            borderRadius: "50%",
            background:
              "linear-gradient(45deg, rgba(51, 51, 51, 0.2), rgba(102, 102, 102, 0.05))",
            filter: "blur(60px)",
            animation: "float 8s ease-in-out infinite reverse",
            "@media (max-width: 768px)": {
              display: "none",
            },
          }}
        />

        {/* Logo */}
        <Box
          component="img"
          src="./LogoNew.png"
          alt="Company Logo"
          sx={{
            position: "absolute",
            top: 40,
            left: 40,
            width: "120px",
            height: "auto",
            zIndex: 10,
            filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.5))",
            "@media (max-width: 1024px) and (min-width: 768px)": {
              top: 20,
              left: 20,
              width: "60px",
              zIndex: 15,
            },
          }}
        />

        {/* Darren Image - Main Focus */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(45deg, rgba(0,0,0,0.3) 0%, transparent 50%, rgba(51, 51, 51, 0.1) 100%)",
              zIndex: 1,
            },
          }}
        >
          <Box
            component="img"
            src="./DarrenImage.png"
            alt="Darren Vermost"
            sx={{
              maxHeight: "85vh",
              maxWidth: "90%",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              borderRadius: "20px",
              boxShadow:
                "0 20px 60px rgba(0,0,0,0.8), 0 0 40px rgba(51, 51, 51, 0.2)",
              border: "2px solid rgba(51, 51, 51, 0.3)",
              zIndex: 2,
              filter: "contrast(1.1) brightness(1.05)",
              "@media (max-width: 768px)": {
                boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                border: "none",
                filter: "none",
              },
            }}
          />
        </Box>
      </Box>

      {/* Right Column - Registration Form */}
      <Box
        sx={{
          width: isMobile ? "100%" : "40%",
          display: "flex",
          paddingTop: "2vh",
          paddingBottom: "140px",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: "#fafafa",
          position: "relative",
          height: "100%",
          overflowY: "auto",
          background: isMobile
            ? "white"
            : "linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%)",
          "&::before": isMobile
            ? {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "transparent",
              pointerEvents: "none",
            }
            : {},
        }}
      >
        {/* Registration Form Container */}
        <Box
          sx={{
            width: "90%",
            maxWidth: "480px",
            backgroundColor: "transparent",
            p: { xs: 3, sm: 4 },
            pb: 20,
            position: "relative",
          }}
        >
          {/* Company Logo — matches Login page */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Box
              component="img"
              src="./ProbateBondPros.png"
              alt="Probate Bond Pros Logo"
              sx={{
                height: 60,
                maxWidth: "100%",
                filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.2))",
              }}
            />
          </Box>

          {/* Back Button — absolute positioned like Login page */}
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Box
              sx={{
                position: "absolute",
                top: 40,
                left: 0,
                "@media (max-width: 768px)": {
                  left: -10,
                },
              }}
            >
              <IconButton
                onClick={() => {
                  if (userType) {
                    setUserType("");
                    setRegistrationStep(1);
                    setCorporateContacts([]);
                    setGuestReferralSource("");
                    setGuestReferralName("");
                  } else {
                    navigate("/");
                  }
                }}
                sx={{
                  color: primaryColor,
                  backgroundColor: "rgba(51, 51, 51, 0.1)",
                  "&:hover": {
                    backgroundColor: "rgba(51, 51, 51, 0.2)",
                  },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Welcome Section — matches Login page typography */}
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: primaryColor,
                mb: 0.5,
                fontSize: { xs: "1.6rem", sm: "1.8rem" },
              }}
            >
              Create Your Account
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontSize: "1rem",
                fontWeight: 400,
              }}
            >
              Join our platform today
            </Typography>
          </Box>

          {/* User Type Selection */}
          {!userType && (
            <Box>
              <FormControl component="fieldset" fullWidth>
                <FormLabel
                  component="legend"
                  sx={{
                    fontWeight: 600,
                    color: primaryColor,
                    mb: 1.5,
                    fontSize: "1rem",
                  }}
                >
                  Who is registering today?
                </FormLabel>
                <RadioGroup
                  value={userType}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "individual_bond_request") {
                      setUserType("individual_bond_request");
                    } else if (value === "corporate_pg") {
                      setUserType("professional_guardian");
                      setFormData(prev => ({ ...prev, is_corporate_pg: true }));
                    } else if (value === "individual_pg") {
                      setUserType("professional_guardian");
                      setFormData(prev => ({ ...prev, is_corporate_pg: false }));
                    } else {
                      setUserType(value);
                    }
                  }}
                  sx={{ gap: 1 }}
                >
                  {[
                    { value: "lawfirm_user", title: "Attorney or law firm staff", subtitle: "I work with a law firm" },
                    { value: "individual_pg", title: "Professional Guardian (Individual)", subtitle: "I am an independent professional guardian" },
                    { value: "corporate_pg", title: "Corporate Professional Guardian", subtitle: "I am registering for a corporate guardian entity" },
                    { value: "independent_paralegal", title: "Independent Paralegal", subtitle: "I provide paralegal services to law firms" },
                    { value: "individual_bond_request", title: "Individual bond request", subtitle: "I need a bond and am not a professional guardian or attorney" },
                  ].map((opt) => (
                    <FormControlLabel
                      key={opt.value}
                      value={opt.value}
                      control={<Radio size="small" sx={{ color: primaryColor, p: 0.75 }} />}
                      label={
                        <Box sx={{ py: 0.25 }}>
                          <Typography variant="body1" fontWeight={600} sx={{ lineHeight: 1.4 }}>
                            {opt.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                            {opt.subtitle}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        border: "2px solid #E0E0E0",
                        borderRadius: "0px",
                        px: 1.5,
                        py: 1,
                        m: 0,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "rgba(51, 51, 51, 0.04)",
                          borderColor: primaryColor,
                        },
                      }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          )}

          {/* Individual Bond Request — How did you find us? + redirect to guest flow */}
          {userType === "individual_bond_request" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor }}>
                How did you find us?
              </Typography>
              <Autocomplete
                options={["Google Search", "Referred by a law firm", "Referred by a court", "Referred by an attorney", "Word of mouth", "Other"]}
                value={guestReferralSource || null}
                onChange={(_, newValue) => setGuestReferralSource(newValue || "")}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select one..." sx={textFieldSx} />
                )}
              />
              {guestReferralSource && guestReferralSource.toLowerCase().includes("referred") && (
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                      Who referred you?
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    variant="outlined"
                    value={guestReferralName}
                    onChange={(e) => setGuestReferralName(e.target.value)}
                    placeholder="e.g. Smith Law Firm, Miami-Dade Court"
                    sx={textFieldSx}
                  />
                </Box>
              )}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 0.5 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    const params = new URLSearchParams();
                    if (guestReferralSource) params.set("source", guestReferralSource);
                    if (guestReferralName) params.set("referred_by", guestReferralName);
                    const qs = params.toString();
                    navigate(`/guest-request${qs ? `?${qs}` : ""}`);
                  }}
                  sx={{
                    flex: 1,
                    borderRadius: "0px",
                    py: 1.2,
                    fontSize: "1rem",
                    fontWeight: 600,
                    bgcolor: primaryColor,
                    color: "white",
                    boxShadow: "0 8px 32px rgba(51, 51, 51, 0.3)",
                    "&:hover": {
                      bgcolor: secondaryColor,
                      boxShadow: "0 12px 40px rgba(51, 51, 51, 0.4)",
                      transform: "translateY(-1px)",
                    },
                    textTransform: "none",
                    transition: "all 0.3s ease",
                  }}
                >
                  Continue to Bond Request
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setUserType("");
                    setGuestReferralSource("");
                    setGuestReferralName("");
                  }}
                  sx={{
                    flex: 1,
                    borderRadius: "0px",
                    py: 1.2,
                    fontSize: "1rem",
                    fontWeight: 600,
                    borderColor: primaryColor,
                    color: primaryColor,
                    borderWidth: "2px",
                    "&:hover": {
                      borderColor: secondaryColor,
                      backgroundColor: "rgba(51, 51, 51, 0.08)",
                      borderWidth: "2px",
                      transform: "translateY(-1px)",
                    },
                    textTransform: "none",
                    transition: "all 0.3s ease",
                  }}
                >
                  Back to Selection
                </Button>
              </Box>
            </Box>
          )}

          {/* Registration Form */}
          {userType && userType !== "individual_bond_request" && (
            <Box
              component="form"
              sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
            >
              {registrationStep === 1 && (
              <>
              {/* Law Firm Selection - for law firm users and independent paralegals */}
              {(userType === "lawfirm_user" || userType === "independent_paralegal") && (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor, mt: 0.75 }}>
                    Law Firm Information
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, color: "text.primary" }}
                    >
                      Search & Select Law Firm
                    </Typography>
                    <Tooltip title="Search for your law firm from our database" placement="top" arrow>
                      <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                    <Autocomplete
                      key={`law-firm-autocomplete-${allLawFirms.length}`}
                      sx={{ flex: 1 }}
                      options={filteredLawFirms}
                      getOptionLabel={(option) => option.label}
                      value={allLawFirms.find(firm => firm.value === lawFirmData.lawFirm) || null}
                      onChange={(_, newValue) => {
                        setLawFirmData(prev => ({
                          ...prev,
                          lawFirm: newValue?.value || null,
                          lawFirmLocation: null,
                        }));
                        if (errors.lawFirm) {
                          setErrors(prev => ({ ...prev, lawFirm: "" }));
                        }
                        if (errors.lawFirmLocation) {
                          setErrors(prev => ({ ...prev, lawFirmLocation: "" }));
                        }
                      }}
                      onInputChange={(_, newInputValue) => {
                        setLawFirmSearch(newInputValue);
                      }}
                      filterOptions={(x) => x} // Disable built-in filtering since we're doing it manually
                      noOptionsText={
                        lawFirmSearch.length > 0 && lawFirmSearch.length < 3
                          ? "Type at least 3 characters to search"
                          : filteredLawFirms.length === 0 && lawFirmSearch.length >= 3
                            ? "No law firms found"
                            : "Start typing to search law firms"
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Type to search for law firms..."
                          error={!!errors.lawFirm}
                          helperText={errors.lawFirm}
                          required
                          sx={{
                            ...textFieldSx,
                            "& .MuiOutlinedInput-root": {
                              ...textFieldSx["& .MuiOutlinedInput-root"],
                              "& fieldset": {
                                borderColor: errors.lawFirm ? "#d32f2f" : "#E0E0E0",
                                borderWidth: "2px",
                              },
                              "&:hover fieldset": {
                                borderColor: errors.lawFirm ? "#d32f2f" : primaryColor,
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: errors.lawFirm ? "#d32f2f" : primaryColor,
                              },
                            },
                          }}
                          slotProps={createLoadingAutocompleteSlotProps(loadingLawFirms, params, {
                            spinnerSize: 20,
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ color: "text.secondary" }} />
                              </InputAdornment>
                            ),
                          })}
                        />
                      )}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleOpenLawFirmDialog}
                      sx={{
                        height: COMPONENT_HEIGHT,
                        minWidth: COMPONENT_HEIGHT,
                        borderRadius: "0px",
                        borderColor: primaryColor,
                        color: primaryColor,
                        borderWidth: "2px",
                        "&:hover": {
                          borderColor: secondaryColor,
                          backgroundColor: "rgba(51, 51, 51, 0.08)",
                          borderWidth: "2px",
                        },
                      }}
                    >
                      <AddIcon />
                    </Button>
                  </Box>

                  {/* Law firm location selection — shown when a law firm is selected */}
                  {lawFirmData.lawFirm && (
                    <Box sx={{ mt: 0.85 }}>
                      <FormControl component="fieldset" error={!!errors.lawFirmLocation} fullWidth>
                        <FormLabel component="legend" sx={{ fontWeight: 600, color: primaryColor, mb: 0.5 }}>
                          Law firm location
                        </FormLabel>
                        {loadingLawFirmLocations ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" color="text.secondary">Loading locations...</Typography>
                          </Box>
                        ) : lawFirmLocations.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">No locations found for this law firm.</Typography>
                        ) : (
                          <>
                            {selectedLawFirmLocation ? (
                              <Box
                                sx={{
                                  border: "2px solid",
                                  borderColor: "divider",
                                  borderRadius: "0px",
                                  p: 1.5,
                                  backgroundColor: "grey.50",
                                }}
                              >
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {selectedLawFirmLocation.name || "—"}
                                  </Typography>
                                  <Button
                                    size="small"
                                    onClick={() => setLawFirmLocationDialogOpen(true)}
                                    sx={{
                                      p: 0,
                                      minWidth: "auto",
                                      borderRadius: "0px",
                                      color: primaryColor,
                                      textTransform: "none",
                                      fontWeight: 600,
                                      fontSize: "0.85rem",
                                      "&:hover": { backgroundColor: "transparent", textDecoration: "underline" }
                                    }}
                                  >
                                    Change location
                                  </Button>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                  {[selectedLawFirmLocation.address, selectedLawFirmLocation.city, selectedLawFirmLocation.state, selectedLawFirmLocation.zip_code].filter(Boolean).join(", ")}
                                </Typography>
                              </Box>
                            ) : (
                              <Button
                                variant="outlined"
                                onClick={() => setLawFirmLocationDialogOpen(true)}
                                sx={{
                                  height: COMPONENT_HEIGHT,
                                  borderRadius: "0px",
                                  borderColor: primaryColor,
                                  color: primaryColor,
                                  borderWidth: "2px",
                                  textTransform: "none",
                                  fontWeight: 600,
                                  "&:hover": { borderColor: secondaryColor, backgroundColor: "rgba(51, 51, 51, 0.08)", borderWidth: "2px" },
                                }}
                              >
                                Select location
                              </Button>
                            )}
                            {errors.lawFirmLocation && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>{errors.lawFirmLocation}</Typography>
                            )}
                          </>
                        )}
                      </FormControl>
                    </Box>
                  )}

                  {/* Law firm location selection dialog with table */}
                  <Dialog
                    open={lawFirmLocationDialogOpen}
                    onClose={() => setLawFirmLocationDialogOpen(false)}
                    maxWidth="md"
                    fullWidth
                    slotProps={{ paper: { sx: { borderRadius: "0px" } } }}
                  >
                    <DialogTitle sx={{ fontWeight: 600, color: primaryColor }}>Select law firm location</DialogTitle>
                    <DialogContent dividers>
                      {loadingLawFirmLocations ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 4 }}>
                          <CircularProgress size={24} />
                          <Typography variant="body2" color="text.secondary">Loading locations...</Typography>
                        </Box>
                      ) : lawFirmLocations.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">No locations found for this law firm.</Typography>
                      ) : (
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: "0px" }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700, backgroundColor: "grey.100" }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 700, backgroundColor: "grey.100" }}>Address</TableCell>
                                <TableCell sx={{ fontWeight: 700, backgroundColor: "grey.100" }}>City</TableCell>
                                <TableCell sx={{ fontWeight: 700, backgroundColor: "grey.100" }}>State</TableCell>
                                <TableCell sx={{ fontWeight: 700, backgroundColor: "grey.100" }}>Zip</TableCell>
                                <TableCell sx={{ fontWeight: 700, backgroundColor: "grey.100", width: 100 }} align="right">Action</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {lawFirmLocations.map((loc) => (
                                <TableRow
                                  key={loc.value}
                                  hover
                                  selected={lawFirmData.lawFirmLocation === loc.value}
                                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                >
                                  <TableCell>{loc.name || "—"}</TableCell>
                                  <TableCell>{loc.address || "—"}</TableCell>
                                  <TableCell>{loc.city || "—"}</TableCell>
                                  <TableCell>{loc.state || "—"}</TableCell>
                                  <TableCell>{loc.zip_code || "—"}</TableCell>
                                  <TableCell align="right">
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => {
                                        setLawFirmData(prev => ({ ...prev, lawFirmLocation: loc.value }));
                                        if (errors.lawFirmLocation) setErrors(prev => ({ ...prev, lawFirmLocation: "" }));
                                        setLawFirmLocationDialogOpen(false);
                                      }}
                                      sx={{
                                        borderRadius: "0px",
                                        backgroundColor: primaryColor,
                                        "&:hover": { backgroundColor: secondaryColor },
                                      }}
                                    >
                                      Select
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, py: 2 }}>
                      <Button
                        onClick={() => setLawFirmLocationDialogOpen(false)}
                        sx={{ borderRadius: "0px", color: primaryColor }}
                      >
                        Cancel
                      </Button>
                    </DialogActions>
                  </Dialog>

                  <Divider sx={{ my: 0.75 }} />
                </>
              )}

              {/* Personal Information */}
              <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor, mt: 0.75 }}>
                Personal Information
              </Typography>

              {/* Common Fields */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                      First Name
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    variant="outlined"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    onBlur={validateFirstName}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    placeholder="First name"
                    required
                    sx={textFieldSx}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                      Last Name
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    variant="outlined"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    onBlur={validateLastName}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    placeholder="Last name"
                    required
                    sx={textFieldSx}
                  />
                </Box>
              </Box>

              {/* Type-Specific Contact Info */}
              {["professional_guardian", "independent_paralegal"].includes(userType) && (
                <>
                  <Divider sx={{ my: 0.75 }} />

                  {userType === "professional_guardian" && formData.is_corporate_pg && (
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                          Business Name
                        </Typography>
                      </Box>
                      <TextField
                        fullWidth
                        variant="outlined"
                        name="business_name"
                        value={formData.business_name}
                        onChange={handleInputChange}
                        onBlur={validateBusinessName}
                        error={!!errors.business_name}
                        helperText={errors.business_name}
                        placeholder="Enter legal business name"
                        required
                        sx={textFieldSx}
                      />
                    </Box>
                  )}


                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                          Phone Number
                        </Typography>
                      </Box>
                      <TextField
                        fullWidth
                        variant="outlined"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        onBlur={validatePhoneNumber}
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber}
                        placeholder="(555) 555-5555"
                        required
                        sx={textFieldSx}
                      />
                    </Box>
                    {!(userType === "professional_guardian" && formData.is_corporate_pg) && (
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                            Date of Birth
                          </Typography>
                        </Box>
                        <TextField
                          fullWidth
                          type="date"
                          variant="outlined"
                          name="dateOfBirth"
                          value={formData.dateOfBirth}
                          onChange={handleInputChange}
                          onBlur={validateDateOfBirth}
                          error={!!errors.dateOfBirth}
                          helperText={errors.dateOfBirth}
                          slotProps={{ inputLabel: { shrink: true } }}
                          required
                          sx={textFieldSx}
                        />
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ mt: 0.75 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                        Mailing Address
                      </Typography>
                    </Box>
                    <GoogleAutoComplete
                      placeholder="Search for your address..."
                      callback={handleAddressCallback}
                    />
                    <TextField
                      fullWidth
                      variant="outlined"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      onBlur={validateAddress}
                      error={!!errors.address}
                      helperText={errors.address}
                      placeholder="Mailing address"
                      required
                      sx={{ ...textFieldSx, mt: 0.5 }}
                    />
                  </Box>
                </>
              )}

              {/* Position Field (only for law firm users) */}
              {userType === "lawfirm_user" && (
                <Box sx={{ mt: 0.75 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                      Position
                    </Typography>
                  </Box>
                  <Autocomplete
                    options={["Attorney", "Paralegal", "Staff"]}
                    value={formData.position || null}
                    onChange={(_, newValue) => {
                      setFormData(prev => ({ ...prev, position: newValue || "" }));
                      if (errors.position) setErrors(prev => ({ ...prev, position: "" }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!errors.position}
                        helperText={errors.position}
                        placeholder="Select position"
                        required
                        sx={textFieldSx}
                      />
                    )}
                  />
                </Box>
              )}

              <Divider sx={{ my: 0.75 }} />

              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                    Email Address
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  variant="outlined"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={() =>
                    duplicateEmailCheck.checkEmailOnBlur(validateEmail, formData.email)
                  }
                  error={!!errors.email}
                  helperText={errors.email}
                  placeholder="name@example.com"
                  required
                  sx={textFieldSx}
                />
                <DuplicateEmailAlert
                  {...duplicateEmailCheck.getAlertProps(formData.email)}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                      Username
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    variant="outlined"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    onBlur={validateUsername}
                    error={!!errors.username}
                    helperText={errors.username}
                    placeholder="Choose username"
                    required
                    sx={textFieldSx}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary" }}>
                      Password
                    </Typography>
                  </Box>
                  <RegistrationPasswordField
                    fullWidth
                    variant="outlined"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onBlur={validatePassword}
                    error={!!errors.password}
                    helperText={errors.password}
                    placeholder="Enter password"
                    required
                    sx={textFieldSx}
                  />
                </Box>
              </Box>
              </>
              )}

              {/* Step 2: Corporate PG Contact Addition */}
              {registrationStep === 2 && userType === "professional_guardian" && formData.is_corporate_pg && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor }}>
                    Add Team Members (Optional)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Add contact users who can access this account. You can also do this later from your dashboard.
                  </Typography>

                  {corporateContacts.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      {corporateContacts.map((c, idx) => (
                        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, border: "1px solid #E0E0E0", borderRadius: "4px", mb: 0.5 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600}>{c.full_name}</Typography>
                            <Typography variant="caption" color="text.secondary">{c.email} {c.is_admin ? "(Admin)" : ""}</Typography>
                          </Box>
                          <IconButton size="small" onClick={() => setCorporateContacts(prev => prev.filter((_, i) => i !== idx))}>
                            <ArrowBackIcon sx={{ fontSize: "1rem", transform: "rotate(0deg)" }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}

                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <TextField fullWidth placeholder="Full Name" value={contactForm.full_name} onChange={(e) => setContactForm(prev => ({ ...prev, full_name: e.target.value }))} error={!!contactErrors.full_name} helperText={contactErrors.full_name} sx={textFieldSx} />
                    <TextField fullWidth placeholder="Email" value={contactForm.email} onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))} error={!!contactErrors.email} helperText={contactErrors.email} sx={textFieldSx} />
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", mt: 0.5 }}>
                    <TextField fullWidth placeholder="Phone (optional)" value={contactForm.phone_number} onChange={(e) => setContactForm(prev => ({ ...prev, phone_number: formatPhoneNumber(e.target.value) }))} sx={textFieldSx} />
                    <FormControlLabel
                      control={<Checkbox checked={contactForm.is_admin} onChange={(e) => setContactForm(prev => ({ ...prev, is_admin: e.target.checked }))} size="small" />}
                      label={<Typography variant="body2">Admin</Typography>}
                      sx={{ minWidth: "90px" }}
                    />
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const errs = validateCorporateContactForm(contactForm);
                      setContactErrors(errs);
                      if (Object.keys(errs).length > 0) return;
                      setCorporateContacts(prev => [...prev, { ...contactForm }]);
                      setContactForm({ full_name: "", email: "", phone_number: "", is_admin: false });
                      setContactErrors({});
                    }}
                    sx={{ mt: 0.5, borderRadius: "0px", borderColor: primaryColor, color: primaryColor, textTransform: "none", fontWeight: 600, height: COMPONENT_HEIGHT }}
                  >
                    + Add Contact
                  </Button>
                </>
              )}

              {/* Submit Buttons */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                {registrationStep === 2 ? (
                  <>
                    <Button
                      variant="contained"
                      disabled={firstTimeSubmitDisabled}
                      onClick={handleRegister}
                      sx={{
                        borderRadius: "0px",
                        py: 1.2,
                        fontSize: "1rem",
                        fontWeight: 600,
                        bgcolor: primaryColor,
                        color: "white",
                        boxShadow: "0 8px 32px rgba(51, 51, 51, 0.3)",
                        "&:hover": {
                          bgcolor: secondaryColor,
                          boxShadow: "0 12px 40px rgba(51, 51, 51, 0.4)",
                          transform: "translateY(-1px)",
                        },
                        textTransform: "none",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : corporateContacts.length > 0 ? "Create Account & Invite" : "Create Account"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setRegistrationStep(1)}
                      disabled={loading}
                      sx={{
                        borderRadius: "0px",
                        py: 1.2,
                        fontSize: "1rem",
                        fontWeight: 600,
                        borderColor: primaryColor,
                        color: primaryColor,
                        borderWidth: "2px",
                        "&:hover": {
                          borderColor: secondaryColor,
                          backgroundColor: "rgba(51, 51, 51, 0.08)",
                          borderWidth: "2px",
                          transform: "translateY(-1px)",
                        },
                        textTransform: "none",
                        transition: "all 0.3s ease",
                      }}
                    >
                      Back
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      onClick={handleRegister}
                      disabled={firstTimeSubmitDisabled}
                      sx={{
                        borderRadius: "0px",
                        py: 1.2,
                        fontSize: "1rem",
                        fontWeight: 600,
                        bgcolor: primaryColor,
                        color: "white",
                        boxShadow: "0 8px 32px rgba(51, 51, 51, 0.3)",
                        "&:hover": {
                          bgcolor: secondaryColor,
                          boxShadow: "0 12px 40px rgba(51, 51, 51, 0.4)",
                          transform: "translateY(-1px)",
                        },
                        textTransform: "none",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : (userType === "professional_guardian" && formData.is_corporate_pg) ? "Next: Add Team Members" : "Create Account"}
                    </Button>

                    <Button
                      variant="outlined"
                      onClick={() => {
                        if (userType) {
                          setUserType("");
                          setRegistrationStep(1);
                          setCorporateContacts([]);
                        } else {
                          navigate("/");
                        }
                      }}
                      sx={{
                        borderRadius: "0px",
                        py: 1.2,
                        fontSize: "1rem",
                        fontWeight: 600,
                        borderColor: primaryColor,
                        color: primaryColor,
                        borderWidth: "2px",
                        "&:hover": {
                          borderColor: secondaryColor,
                          backgroundColor: "rgba(51, 51, 51, 0.08)",
                          borderWidth: "2px",
                          transform: "translateY(-1px)",
                        },
                        textTransform: "none",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {userType ? "Back to Selection" : "Back to Login"}
                    </Button>
                  </>
                )}

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Divider sx={{ flex: 1 }} />
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>or</Typography>
                  <Divider sx={{ flex: 1 }} />
                </Box>

                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Already have an account?{" "}
                    <Link
                      href="#"
                      onClick={(e) => { e.preventDefault(); navigate("/"); }}
                      sx={{
                        fontWeight: 600,
                        color: primaryColor,
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      Sign In
                    </Link>
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Law Firm Creation Dialog */}
      <Dialog open={openLawFirmDialog} onClose={handleCloseLawFirmDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, color: primaryColor }}>
          Create New Law Firm & Location
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Law Firm Information */}
            <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor, mb: 1 }}>
              Law Firm Information
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Law Firm Name
              </Typography>
              <Tooltip title="Enter the name of the law firm" placement="top" arrow>
                <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              value={newLawFirm.name}
              onChange={(e) => setNewLawFirm({ ...newLawFirm, name: e.target.value })}
              placeholder="Enter law firm name"
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0px",
                  backgroundColor: "rgba(249, 249, 249, 0.8)",
                  "& fieldset": {
                    borderColor: "#E0E0E0",
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": {
                    borderColor: primaryColor,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: primaryColor,
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "text.secondary",
                  opacity: 0.7,
                },
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Website (Optional)
              </Typography>
              <Tooltip title="Enter the law firm's website URL" placement="top" arrow>
                <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              value={newLawFirm.website}
              onChange={(e) => setNewLawFirm({ ...newLawFirm, website: e.target.value })}
              placeholder="https://www.lawfirm.com"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0px",
                  backgroundColor: "rgba(249, 249, 249, 0.8)",
                  "& fieldset": {
                    borderColor: "#E0E0E0",
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": {
                    borderColor: primaryColor,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: primaryColor,
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "text.secondary",
                  opacity: 0.7,
                },
              }}
            />

            <Divider sx={{ my: 2 }} />

            {/* Location Information */}
            <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor, mb: 1 }}>
              Main Location Information
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Search Address
              </Typography>
              <Tooltip title="Search for the law firm's main office address" placement="top" arrow>
                <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <GoogleAutoComplete
              key={`law-firm-address-${openLawFirmDialog}`}
              placeholder="Search Office Address"
              callback={handleLawFirmAddressCallback}
            />

            {/* Individual Address Fields */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, mt: 2 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Street Address
              </Typography>
              <Tooltip title="Street address of the law firm" placement="top" arrow>
                <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              value={newLawFirm.address}
              onChange={(e) => setNewLawFirm(prev => ({ ...prev, address: e.target.value }))}
              placeholder="Street address"
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0px",
                  backgroundColor: "rgba(249, 249, 249, 0.8)",
                  "& fieldset": {
                    borderColor: "#E0E0E0",
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": {
                    borderColor: primaryColor,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: primaryColor,
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "text.secondary",
                  opacity: 0.7,
                },
              }}
            />

            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    City
                  </Typography>
                  <Tooltip title="City where the law firm is located" placement="top" arrow>
                    <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Autocomplete
                  options={allCities}
                  loading={loadingCitiesStates}
                  value={
                    allCities.includes(newLawFirm.city) ? newLawFirm.city : null
                  }
                  onChange={(_, newValue) =>
                    setNewLawFirm((prev) => ({ ...prev, city: newValue || "" }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      variant="outlined"
                      placeholder="City"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "0px",
                          backgroundColor: "rgba(249, 249, 249, 0.8)",
                          "& fieldset": {
                            borderColor: "#E0E0E0",
                            borderWidth: "2px",
                          },
                          "&:hover fieldset": {
                            borderColor: primaryColor,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: primaryColor,
                          },
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: "text.secondary",
                          opacity: 0.7,
                        },
                      }}
                      slotProps={createLoadingAutocompleteSlotProps(loadingCitiesStates, params)}
                    />
                  )}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    State
                  </Typography>
                  <Tooltip title="State where the law firm is located" placement="top" arrow>
                    <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Autocomplete
                  options={allStates}
                  loading={loadingCitiesStates}
                  value={
                    allStates.includes(newLawFirm.state) ? newLawFirm.state : null
                  }
                  onChange={(_, newValue) =>
                    setNewLawFirm((prev) => ({ ...prev, state: newValue || "" }))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      variant="outlined"
                      placeholder="State"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "0px",
                          backgroundColor: "rgba(249, 249, 249, 0.8)",
                          "& fieldset": {
                            borderColor: "#E0E0E0",
                            borderWidth: "2px",
                          },
                          "&:hover fieldset": {
                            borderColor: primaryColor,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: primaryColor,
                          },
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: "text.secondary",
                          opacity: 0.7,
                        },
                      }}
                      slotProps={createLoadingAutocompleteSlotProps(loadingCitiesStates, params)}
                    />
                  )}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    ZIP Code
                  </Typography>
                  <Tooltip title="ZIP code of the law firm's location" placement="top" arrow>
                    <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={newLawFirm.zip}
                  onChange={(e) => setNewLawFirm(prev => ({ ...prev, zip: e.target.value }))}
                  placeholder="ZIP"
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0px",
                      backgroundColor: "rgba(249, 249, 249, 0.8)",
                      "& fieldset": {
                        borderColor: "#E0E0E0",
                        borderWidth: "2px",
                      },
                      "&:hover fieldset": {
                        borderColor: primaryColor,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: primaryColor,
                      },
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "text.secondary",
                      opacity: 0.7,
                    },
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                Phone Number (Optional)
              </Typography>
              <Tooltip title="Enter the main office phone number" placement="top" arrow>
                <IconButton size="small" sx={{ color: "text.secondary", p: 0 }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              value={newLawFirm.phone}
              onChange={(e) => setNewLawFirm({ ...newLawFirm, phone: formatPhoneNumber(e.target.value) })}
              placeholder="(555) 123-4567"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0px",
                  backgroundColor: "rgba(249, 249, 249, 0.8)",
                  "& fieldset": {
                    borderColor: "#E0E0E0",
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": {
                    borderColor: primaryColor,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: primaryColor,
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "text.secondary",
                  opacity: 0.7,
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseLawFirmDialog}
            variant="outlined"
            sx={{
              borderRadius: "0px",
              py: 1.2,
              px: 3,
              fontSize: "0.9rem",
              fontWeight: 600,
              borderColor: primaryColor,
              color: primaryColor,
              borderWidth: "2px",
              "&:hover": {
                borderColor: secondaryColor,
                backgroundColor: "rgba(51, 51, 51, 0.08)",
                borderWidth: "2px",
              },
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddLawFirm}
            variant="contained"
            disabled={loadingLawFirms || !newLawFirm.name.trim() || !newLawFirm.address.trim() || !newLawFirm.city.trim() || !newLawFirm.state.trim() || !newLawFirm.zip.trim()}
            sx={{
              borderRadius: "0px",
              py: 1.2,
              px: 3,
              fontSize: "0.9rem",
              fontWeight: 600,
              bgcolor: primaryColor,
              color: "white",
              boxShadow: "0 8px 32px rgba(51, 51, 51, 0.3)",
              "&:hover": {
                bgcolor: secondaryColor,
                boxShadow: "0 12px 40px rgba(51, 51, 51, 0.4)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                bgcolor: "rgba(51, 51, 51, 0.3)",
                color: "rgba(255, 255, 255, 0.5)",
              },
              textTransform: "none",
              transition: "all 0.3s ease",
            }}
          >
            {loadingLawFirms ? <CircularProgress size={20} color="inherit" /> : "Create Law Firm & Location"}
          </Button>
        </DialogActions>
      </Dialog >

      <Dialog open={fuzzyMatchDialogOpen} onClose={() => { setFuzzyMatchDialogOpen(false); setFuzzyMatchResult(null); }}>
        <DialogTitle>Similar law firm found</DialogTitle>
        <DialogContent>
          <Typography>
            A similar law firm was found: <strong>{fuzzyMatchResult?.name}</strong>. Do you want to use this existing firm or create a new one?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUseExistingLawFirm} color="primary">
            Use existing
          </Button>
          <Button onClick={handleCreateNewLawFirmAnyway} color="secondary">
            Create new anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Calculator Tab at Bottom */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10,
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          pr: 2,
          gap: 1,
          flexWrap: "wrap",
          pointerEvents: "none",
        }}
      >
        {/* Support Tab */}
        <Box
          onClick={() => setOpenSupport(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            backgroundColor: "#D3D3D3",
            color: "#333",
            px: 4,
            py: 2,
            borderRadius: "8px 8px 0 0",
            cursor: "pointer",
            pointerEvents: "auto",
            minWidth: "300px",
            maxWidth: "400px",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "#C8C8C8",
              transform: "translateY(-2px)",
              boxShadow: "0 -6px 16px rgba(0, 0, 0, 0.2)",
            },
            "@media (max-width: 1024px)": {
              minWidth: "260px",
              maxWidth: "340px",
              px: 3,
              py: 1.5,
              gap: 1.5,
            },
            "@media (max-width: 768px)": {
              minWidth: "220px",
              maxWidth: "300px",
              px: 2,
              py: 1,
              gap: 1,
            },
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: "#333",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "2px",
              flexShrink: 0,
              "@media (max-width: 1024px)": {
                width: 18,
                height: 18,
              },
              "@media (max-width: 768px)": {
                width: 16,
                height: 16,
              },
            }}
          >
            <EmailIcon
              sx={{
                fontSize: 14,
                color: "white",
                "@media (max-width: 1024px)": {
                  fontSize: 12,
                },
                "@media (max-width: 768px)": {
                  fontSize: 10,
                },
              }}
            />
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontSize: "1rem",
              fontWeight: 500,
              color: "#333",
              whiteSpace: "nowrap",
              "@media (max-width: 1024px)": {
                fontSize: "0.9rem",
              },
              "@media (max-width: 768px)": {
                fontSize: "0.8rem",
              },
            }}
          >
            Contact Support
          </Typography>
        </Box>

        <Box
          onClick={() => setOpenCalculator(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            backgroundColor: "#D3D3D3",
            color: "#333",
            px: 4,
            py: 2,
            borderRadius: "8px 8px 0 0",
            cursor: "pointer",
            pointerEvents: "auto",
            minWidth: "320px",
            maxWidth: "400px",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "#C8C8C8",
              transform: "translateY(-2px)",
              boxShadow: "0 -6px 16px rgba(0, 0, 0, 0.2)",
            },
            "@media (max-width: 1024px)": {
              minWidth: "280px",
              maxWidth: "350px",
              px: 3,
              py: 1.5,
              gap: 1.5,
            },
            "@media (max-width: 768px)": {
              minWidth: "240px",
              maxWidth: "300px",
              px: 2,
              py: 1,
              gap: 1,
            },
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              backgroundColor: "#333",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "2px",
              flexShrink: 0,
              "@media (max-width: 1024px)": {
                width: 18,
                height: 18,
              },
              "@media (max-width: 768px)": {
                width: 16,
                height: 16,
              },
            }}
          >
            <CalculatorIcon
              sx={{
                fontSize: 14,
                color: "white",
                "@media (max-width: 1024px)": {
                  fontSize: 12,
                },
                "@media (max-width: 768px)": {
                  fontSize: 10,
                },
              }}
            />
          </Box>
          <Typography
            variant="body1"
            sx={{
              fontSize: "1rem",
              fontWeight: 500,
              color: "#333",
              whiteSpace: "nowrap",
              "@media (max-width: 1024px)": {
                fontSize: "0.9rem",
              },
              "@media (max-width: 768px)": {
                fontSize: "0.8rem",
              },
            }}
          >
            Use our Bond Premium Calculator
          </Typography>
        </Box>
      </Box>

      <SupportContactDialog
        open={openSupport}
        onClose={() => setOpenSupport(false)}
        context="First Time Register"
      />

      {/* Calculator Dialog */}
      <Dialog
        open={openCalculator}
        onClose={() => setOpenCalculator(false)}
        sx={{ "& .MuiPaper-root": { borderRadius: "20px", maxWidth: "90vw" } }}
      >
        <DialogContent>
          <PremiumCalculator />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenCalculator(false)}
            sx={{
              borderRadius: "12px",
              color: primaryColor,
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "rgba(51, 51, 51, 0.08)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </Box >
  );
};

export default FirstTimeRegister; 